import { useCallback, useEffect, useRef, useState } from 'react'
import { useCurrentRef } from './useCurrentRef'
import { useEvent } from './useEvent'
import { useComponentWidth } from './useComponentWidth'
import anylogger from '@app/anylogger'

const log = anylogger('useSoftWrappedLines')

export type SoftWrappedLinesResponse = {
	softWrappedLines: string[]
	getAdjustedOffset: (offset: number) => number
}
/**
 * This hook inserts separatorChar wherever a soft line break occurs in the text based on the width of the refElement.
 * It is useful for determing how many "extra lines" needed to be added when using text overlays
 * It returns the same as passed in by value, but broken into new lines.  Do not use a newLine (\n) as the separatorChar,
 * as you will then not be able to tell where the soft line breaks are.
 */
export const useSoftWrappedLines = (refElement: HTMLElement | null, value: string, separatorChar: string): SoftWrappedLinesResponse => {
	const divRef = useRef<HTMLElement>(undefined)
	const [softWrappedLines, setSoftWrappedLines] = useState([] as string[])

	useEffect(() => {
		if (!refElement) return
		const clone = document.createElement('div')
		divRef.current = clone
		document.body.appendChild(clone)
		clone.style.cssText = window.getComputedStyle(refElement).cssText
		clone.style.position = 'absolute'
		clone.style.visibility = 'hidden'
		clone.style.overflowWrap = 'anywhere' // this is needed so that a long word or link (without spaces) gets wrapped properly
		clone.style.left = '-9999px' // Move it off-screen
		clone.style.top = '-9999px'
		clone.style.whiteSpace = 'pre-wrap' // Critical for word wrapping
		clone.style.height = 'auto'
		return () => {
			if (divRef.current) {
				document.body.removeChild(divRef.current)
				divRef.current = undefined
			}
		}
	}, [refElement])

	const textWidth = useComponentWidth(refElement)

	useEffect(() => {
		if (!divRef.current) return
		if (!value || !textWidth) return
		divRef.current.innerHTML = ''
		divRef.current.style.width = `${textWidth}px`
		for (let i = 0; i < value.length; i++) {
			const char = value[i]
			const span = document.createElement('span')
			span.textContent = char

			span.dataset.index = i.toString()
			divRef.current.appendChild(span)
		}

		// Get the visual position of each character
		const visualLines: string[] = []
		let currentLine = ''
		let prevTop = -1

		Array.from(divRef.current.childNodes).forEach((node: ChildNode) => {
			if (!(node instanceof HTMLElement)) return // Ensure node is an HTMLElement
			const rect = node.getBoundingClientRect()
			if (prevTop !== -1 && rect.top > prevTop) {
				// New line detected
				if (visualLines.length && !visualLines[visualLines.length - 1].endsWith('\n'))
					visualLines[visualLines.length - 1] += separatorChar
				visualLines.push(currentLine)
				currentLine = ''
			}
			currentLine += node.textContent
			prevTop = rect.top
		})

		// Add the last line
		if (currentLine) {
			if (visualLines.length && !visualLines[visualLines.length - 1].endsWith('\n'))
				visualLines[visualLines.length - 1] += separatorChar
			visualLines.push(currentLine)
		}
		// log('divRef', divRef, visualLines, textWidth)

		setSoftWrappedLines(visualLines)
	}, [separatorChar, textWidth, value])

	// This function can be used to adjust offsets based on the soft wrapped lines because we are inserting separatorChar for each soft line break.
	const getAdjustedOffset = useCallback(
		(offset: number) => {
			const renderedStr = softWrappedLines?.join('') || ''
			const regex = new RegExp(separatorChar, 'g')
			let startPos = 0
			let len = offset
			let adjusted = 0
			do {
				const str = renderedStr.slice(startPos, len)
				startPos = startPos + len
				const matches = str.match(regex)
				len = matches ? matches.length : 0
				adjusted += len
			} while (len > 0)
			return offset + adjusted
		},
		[separatorChar, softWrappedLines]
	)

	return { softWrappedLines, getAdjustedOffset }
}
export function getAdjustedOffset(renderedLines: string[], offset: number, separatorChar: string) {}
